import {
  ACTION_ON_CONTRACT_URL,
  TRACK_SENT_CONTRACT_URL,
} from "@/utils/constants/constants.js";
import axios from "axios";

export default {
  data: () => ({
    signers: [],
    timeline: [],
    editing_signer_id: null,
    temp_signer_name: "",
    temp_signer_surname: "",
    temp_signer_email: "",
    decline_modal_visible: false,
    decline_reason: "",
    is_accepting: false,
    is_declining: false,
    is_signing_mode: false,
    signing_loading: false,
    signing_error: null,
    new_signer: {
      first_name: "",
      last_name: "",
      email: "",
    },
  }),
  methods: {
    async accept_signer() {
      this.is_accepting = true;

      if (!this.current_submission_id) {
        this.show_error("No active contract to accept");
        this.is_accepting = false;
        return;
      }

      try {
        const response = await axios.post(
          ACTION_ON_CONTRACT_URL,
          {
            action: "sign",
            submission_id: this.current_submission_id,
            user_id: this.received_user_id || this.user_id,
            project_id: this.project_id,
          },
          { headers: this.headers }
        );

        if (response.data.status === "success") {
          this.show_success(response.data.message);
          await this.fetch_contract_status();
          this.add_timeline_event({
            event_type: "signature",
            description: `Document signed by ${this.founder_name}`,
            timestamp: new Date().toISOString(),
            user: this.founder_name,
          });
        } else {
          this.show_error(response.data.message || "Failed to accept contract");
        }
      } catch (error) {
        this.show_error(
          "Error accepting contract: " +
          (error.response?.data?.error || error.message)
        );
      } finally {
        this.is_accepting = false;
      }
    },

    async decline_signer(reason = null) {
      this.is_declining = true;

      if (!this.current_submission_id) {
        this.show_error("No active contract to decline");
        this.is_declining = false;
        return;
      }

      const payload = {
        action: "decline",
        submission_id: this.current_submission_id,
        user_id: this.received_user_id || this.user_id,
        project_id: this.project_id,
        ...(reason && { reason }),
      };

      try {
        const response = await axios.post(ACTION_ON_CONTRACT_URL, payload, {
          headers: this.headers,
        });

        if (response.data.status === "success") {
          this.show_success(response.data.message);
          await this.fetch_contract_status();
          this.add_timeline_event({
            event_type: "decline",
            description:
              `Document declined by ${this.founder_name}` +
              (reason ? ` (Reason: ${reason})` : ""),
            timestamp: new Date().toISOString(),
            user: this.founder_name,
          });
        } else {
          this.show_error(
            response.data.message || "Failed to decline contract"
          );
        }
      } catch (error) {
        this.show_error(
          "Error declining contract: " +
          (error.response?.data?.error || error.message)
        );
      } finally {
        this.is_declining = false;
      }
    },

    open_decline_modal() {
      this.decline_modal_visible = true;
      this.decline_reason = "";
    },

    confirm_decline() {
      if (!this.decline_reason.trim()) {
        this.show_error("Please provide a reason for declining");
        return;
      }

      this.decline_signer(this.decline_reason).finally(() => {
        this.decline_modal_visible = false;
      });
    },

    cancel_decline() {
      this.decline_modal_visible = false;
      this.decline_reason = "";
    },

    open_edit_modal(signer) {
      if (!signer) return;

      this.editing_signer_id = signer.id;
      this.temp_signer_name = signer.name || "";
      this.temp_signer_surname = signer.surname || "";
      this.temp_signer_email = signer.email || "";
    },

    cancel_edit() {
      this.editing_signer_id = null;
    },

    async save_signer_edits(signer) {
      if (!signer) return false;

      const updates = {};

      if (this.temp_signer_name !== signer.name)
        updates.name = this.temp_signer_name;
      if (this.temp_signer_surname !== signer.surname)
        updates.surname = this.temp_signer_surname;
      if (this.temp_signer_email !== signer.email)
        updates.email = this.temp_signer_email;

      if (Object.keys(updates).length === 0) {
        this.cancel_edit();
        return false;
      }

      const success = await this.edit_pending_signer(signer.id, updates);
      if (success) {
        this.editing_signer_id = null;
        return true;
      }
      return false;
    },

    async edit_pending_signer(signer_id, updates) {
      if (!this.validate_signer_edit_inputs(signer_id)) {
        return false;
      }

      const signer = this.signers.find((s) => s.id === signer_id);
      if (!this.validate_signer_status(signer)) {
        return false;
      }

      const payload = {
        signer_id: signer_id,
        submission_id: this.current_submission_id,
        project_id: this.project_id,
        ...updates,
      };

      const response = await axios.post(TRACK_SENT_CONTRACT_URL, payload, {
        headers: this.headers,
        params: { submission_id: this.current_submission_id },
      });

      if (response.data.message) {
        this.show_success("Signer details updated successfully");
        await this.fetch_contract_status();
        this.update_signer_details(signer, updates);
        return true;
      }

      this.show_error(response.data.error || "Failed to update signer");
      return false;
    },

    validate_signer_edit_inputs(signer_id) {
      if (!this.current_submission_id) {
        this.show_error("No active submission found");
        return false;
      }

      if (!signer_id) {
        this.show_error("Signer ID is required");
        return false;
      }

      return true;
    },

    validate_signer_status(signer) {
      if (!signer) {
        this.show_error("Signer not found");
        return false;
      }

      if (!["pending", "awaiting"].includes(signer.status?.toLowerCase())) {
        this.show_error("Only pending signers can be edited");
        return false;
      }

      return true;
    },

    update_signer_details(signer, updates) {
      if (!signer) return;

      if (updates.email) {
        signer.email = updates.email;
      }
      if (updates.name || updates.surname) {
        signer.name = updates.name || signer.name;
        signer.surname = updates.surname || signer.surname;
        signer.full_name = `${signer.name} ${signer.surname}`.trim();
      }
    },
  },
};
